import { useEffect, useId, useRef, ReactNode } from "react";
import { X } from "lucide-react";

/**
 * Shared shell for the item 99 email-click prompt and the item 100 US notice.
 * Non-modal labelled dialog; focus moves to it once on arrival; Esc / × hide
 * it for this page view without storing a choice. The two main buttons are
 * rendered by the caller with identical styling (equal weight).
 */
export function ConsentNotice({
  title,
  children,
  actions,
  footer,
  onDismiss,
}: {
  title: string;
  children: ReactNode;
  actions: ReactNode;
  footer?: ReactNode;
  onDismiss: () => void;
}) {
  const titleId = useId();
  const descId = useId();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    ref.current?.focus({ preventScroll: true });
  }, []);

  return (
    <div
      ref={ref}
      role="dialog"
      aria-modal="false"
      aria-labelledby={titleId}
      aria-describedby={descId}
      tabIndex={-1}
      onKeyDown={(e) => { if (e.key === "Escape") onDismiss(); }}
      className="fixed bottom-0 inset-x-0 z-[60] bg-background/95 backdrop-blur-sm border-t border-border shadow-lg animate-slide-up outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <div className="container mx-auto px-4 py-4 flex flex-col sm:flex-row items-start sm:items-center gap-4 relative">
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Close this message"
          className="absolute top-2 right-2 p-1 rounded text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="flex-1 min-w-0 pr-6">
          <h2 id={titleId} className="sr-only">{title}</h2>
          <div id={descId} className="text-sm text-foreground leading-relaxed">{children}</div>
        </div>
        <div className="flex flex-col gap-2 w-full sm:w-auto flex-shrink-0">
          <div className="flex flex-col min-[360px]:flex-row gap-2">{actions}</div>
          {footer && <div className="text-xs text-center sm:text-right">{footer}</div>}
        </div>
      </div>
    </div>
  );
}
